









































































import {Component, Prop, Vue} from 'vue-property-decorator';
import {updateCart as updateCartService} from '../../utils/shop/http-services';
import {CartLine} from './types';
import LineItems from '../CartOverviewWidget/components/LineItems.vue';
import {CartInformation} from "../CartSubmitWidget/types";

@Component({components: {LineItems}})
export default class CartOverviewWidget extends Vue {
  @Prop() cartlines!: CartLine[];
  @Prop() lineitempositions!: CartLine[];
  @Prop() csrftoken!: string;
  @Prop() nextsteplink!: string;
  @Prop() productlisturl!: string;
  @Prop() cart!: CartInformation;

  errors: any | null = null;
  loading = false;
  private _debounceTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
    
  updateCart(lineItemId: number, variantId: number, qty = -1) {
    // Debounce rapid quantity changes per line item/variant (e.g. fast +/+/+ clicks)
    const key = `${lineItemId}:${variantId}`;
    const existingTimer = this._debounceTimers.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    const timer = setTimeout(() => {
      this._debounceTimers.delete(key);
      this._executeCartUpdate(lineItemId, variantId, qty);
    }, 350);
    this._debounceTimers.set(key, timer);
  }

  private _executeCartUpdate(lineItemId: number, variantId: number, qty = -1) {
    this.loading = true;

    updateCartService(this.csrftoken, this.mapToFormData(lineItemId, variantId, qty))
    .then(({data}) => {
      this.errors = null;
      data.error && (this.errors = data.errors);
      this.updateCartInformation(data.cart, lineItemId, qty <= 0);
      this.onProductChange(data.cart);
    })
    .catch(ex => {
      console.error('Error', ex);
      this.errors = [this.$t('SHOP.CART.ADDRESS.UPDATE_ERROR') as string];
    })
    .finally(() => (this.loading = false));
  }

  mapToFormData(lineItemId: number, variantId: number, qty = -1): FormData {
    const formData = new FormData();

    if (lineItemId) {
      if (qty === -1) {
        formData.append(`lineItems[${lineItemId}][remove]`, '1' as any);
      } else {
        // Update local qty state for the matching variant
        this.cartlines.forEach(cartLine => {
          cartLine.variants?.forEach(variant => {
            if (variant.lineItem.id === lineItemId) {
              variant.lineItem.qty = qty;
            }
          });
        });
        formData.append(`lineItems[${lineItemId}][qty]`, qty as any);
      }
    } else if (variantId) {
      formData.append(`purchasables[${variantId}][id]`, variantId as any);
      formData.append(`purchasables[${variantId}][qty]`, qty as any);
    }

    return formData;
  }

  updateCartInformation(cart: any, lineItemId: number, isRemoving = false) {
    const newCartLines = [...this.cartlines];

    if (isRemoving) {
      newCartLines.forEach((cl, cartLineIndex) => {
        cl.variants?.forEach(variant => {
          if (variant.lineItem.id && variant.lineItem.id === lineItemId) {
            variant.lineItem.id = null;
            variant.lineItem.qty = 0;
            variant.lineItem.subTotal = 0;
            variant.lineItem.formattedSubTotal = "CHF " + (0).toLocaleString('de-CH', {minimumFractionDigits: 2});
          }
        });
        let totalQty = 0;
        cl.variants?.forEach(v => totalQty += v.lineItem.qty ?? 0);
        if (totalQty <= 0) newCartLines.splice(cartLineIndex, 1);
      });

      this.cart.total = cart.itemTotalAsCurrency;
      this.cart.subTotal = cart.itemSubtotalAsCurrency;

      this.cartlines = [...newCartLines];
      return;
    }

    cart.lineItems.forEach((lineItem: any) => {
      newCartLines.forEach(ct => {
        ct.variants?.forEach(variant => {
          if (variant.lineItem.id && variant.lineItem.id === lineItem.id) {
            variant.lineItem.price = lineItem.price;
            variant.lineItem.formattedPrice = lineItem.priceAsCurrency;
            variant.lineItem.subTotal = lineItem.subtotal;
            variant.lineItem.formattedSubTotal = lineItem.subtotalAsCurrency;
          } else if (variant.variant.purchasableId === lineItem.purchasableId) {
            variant.lineItem.id = lineItem.id;
            variant.lineItem.qty = lineItem.qty;
            variant.lineItem.price = lineItem.price;
            variant.lineItem.formattedPrice = lineItem.priceAsCurrency;
            variant.lineItem.subTotal = lineItem.subtotal;
            variant.lineItem.formattedSubTotal = lineItem.subtotalAsCurrency;
          }
        });
      });
    });

    this.cart.total = cart.itemTotalAsCurrency;
    this.cart.subTotal = cart.itemSubtotalAsCurrency;

    this.cartlines = [...newCartLines];
  }

  removeCartLine(cartLine: CartLine) {
    this.loading = true;

    const formData = new FormData();
    cartLine.variants?.forEach(variant => {
      if (!variant.lineItem.id) return;
      formData.append(`lineItems[${variant.lineItem.id}][remove]`, '1' as any)
    });

    updateCartService(this.csrftoken, formData)
    .then(({data}) => {
      this.errors = null;
      data.error && (this.errors = data.errors);

      cartLine.variants?.forEach(variant => {
        if (variant.lineItem.id) {
          this.updateCartInformation(data.cart, variant.lineItem.id, true);
        }
      });
      this.onProductChange(data.cart);
    })
    .catch(ex => {
      console.error('Error', ex);
      this.errors = [this.$t('SHOP.CART.ADDRESS.UPDATE_ERROR') as string];
    })
    .finally(() => this.loading = false);
  }

  onProductChange(cart: any): void {
    const event = new CustomEvent('product-added', { detail: { cart } });
    window.dispatchEvent(event);
  }

  beforeDestroy(): void {
    if (this._debounceTimers.size > 0) {
      this._debounceTimers.forEach(timer => clearTimeout(timer));
      this._debounceTimers = new Map();
    }
  }
}
